<script setup lang="ts">
import { h } from 'vue'
import { Person24Regular, Premium24Regular, SignOut20Regular } from '@vicons/fluent';
import ToolbarComponent from '@/components/ToolbarComponent.vue';
import PermissionSetTagComponent from '@/components/PermissionSetTagComponent.vue';
import { useThemeStore } from '@/stores/themeStore';
import { type SelectOption, useMessage } from 'naive-ui';
import { useOsTheme } from 'naive-ui';
import SettingComponent from '@/components/SettingComponent.vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/userStore';
import { authService } from '@/services/AuthService';
import config from '@/services/ConfigService';

const osTheme = useOsTheme()
const { permissionGroups } = useUserStore()
const { isSuperAdmin, userImpersonation, roles, startPageName, user } = storeToRefs(useUserStore())
const themeStore = useThemeStore()
const message = useMessage()
const permissionSummary = computed(() => permissionGroups.flatMap(x => x.permissions).filter(x => user.value?.permissionSet.permissions.includes(x.value)))
const startPageOptions: SelectOption[] = [
  { label: 'Insights', value: 'insights', disabled: !config.features.insights},
  { label: 'Operators', value: 'groups' },
  { label: 'Dashboard (soon)', value: 'dashboard', disabled: true },
  { label: 'Sales', value: 'sales' },
  { label: 'Contracts', value: 'contracts' },
]
const themeOptions = computed<SelectOption[]>(() => [
  { label: 'Light', value: 'light' },
  { label: 'Dark', value: 'dark' }
])

function onChangeStartPageName(name: string) {
  startPageName.value = name

  message.success('Change was saved')
}
</script>

<template>
  <ToolbarComponent title="Account">
    <template #extra>
      <n-flex>
        <n-button :render-icon="() => h(SignOut20Regular)" @click="authService.logout()">
          Sign out
        </n-button>
      </n-flex>
    </template>
  </ToolbarComponent>

  <n-flex vertical>
    <n-card>
      <template #header>
        <n-thing :title="user?.name">
          <template #avatar>
            <n-avatar :size="50">
              <n-icon>
                <Person24Regular />
              </n-icon>
            </n-avatar>
          </template>
          <template #description>
            <n-flex align="center">
              <span>
                {{ user?.email }}
              </span>
            </n-flex>
          </template>
        </n-thing>
      </template>
      <n-list>
        <n-list-item>
          <SettingComponent label="Permission set">
            <div class="flex items-center gap-2">
              <n-tag v-if="isSuperAdmin" type="success">
                <template #icon>
                  <n-icon :component="Premium24Regular" />
                </template>
                Super admin
              </n-tag>
              <PermissionSetTagComponent :permission-set="user?.permissionSet" size="medium" />
            </div>
          </SettingComponent>
        </n-list-item>
        <n-list-item>
          <SettingComponent label="Permission summary">
            <div v-if="permissionSummary.length">
              <ul style="list-style: disc; margin-left: 1rem;">
                <li v-for="item in permissionSummary" :key="item.value">
                  {{ item.description }}
                </li>
              </ul>
            </div>
            <div v-else>
              No permissions
            </div>
          </SettingComponent>
        </n-list-item>
      </n-list>
    </n-card>
    <n-card title="Preferences">
      <n-list>
        <n-list-item>
          <SettingComponent label="Start page" description="Use this if you regularly go to the same page as soon as you log in">
            <n-select
              v-model:value="startPageName"
              :options="startPageOptions"
              :on-update:value="onChangeStartPageName"
            />
          </SettingComponent>
        </n-list-item>
        <n-list-item>
          <SettingComponent label="Theme" description="Defaults to your system preference">
            <n-radio-group
              v-model:value="themeStore.localSetting"
              :on-update:value="(value: string | null) => themeStore.set(value)"
            >
              <n-radio-button
                v-for="option in themeOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
              <n-radio-button
                :label="`System preferred (currently ${osTheme})`"
                :value="null"
              />
            </n-radio-group>
          </SettingComponent>
        </n-list-item>
        <n-list-item v-if="roles.map(role => role.toLowerCase()).includes('cdi.admin')">
          <SettingComponent
            label="User impersonation"
            description="Temporarily impersonate a non-admin user. This setting resets when the app is reloaded."
          >
            <n-switch v-model:value="userImpersonation" />
          </SettingComponent>
        </n-list-item>
      </n-list>
    </n-card>
  </n-flex>
</template>